import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getAllCustomers,
    deleteCustomer as deleteCustomerAPI,
    getCustomerRewards,
} from '../services/customersApi';

import { createLogger } from '../../../shared/config/logger';
import { addAsyncHandlers } from '../../../shared/store/utils/sliceHelpers';
const logger = createLogger('customersSlice');

export const fetchAllCustomers = createAsyncThunk(
    'customers/fetch',
    async (_, { rejectWithValue }) => {
        try {
            logger.info('Thunk: fetchAllCustomers - started');
            const res = await getAllCustomers();
            // Handle wrapped response: { data: [...] }
            const raw = res.data?.data || res.data || [];
            // Normalize so UI always has total_points / total_visits (API view may return points/visits).
            // Coerce to number so string values from the API display and sort correctly.
            const toNum = (v) => {
                if (v === null || v === undefined || v === '') {
                    return 0;
                }
                const n = typeof v === 'number' ? v : parseFloat(v);
                return Number.isFinite(n) ? n : 0;
            };
            // Prefer points/visits from the wallet pass (same as scan page); total_points may be from a different source.
            const customers = raw.map((c) => ({
                ...c,
                total_points: toNum(c.points ?? c.total_points ?? c.totalPoints),
                total_visits: toNum(c.visits ?? c.total_visits ?? c.totalVisits),
            }));
            logger.info('Thunk: fetchAllCustomers - success', { response_data: customers });
            return customers;
        } catch (error) {
            logger.error('Thunk: fetchAllCustomers - error', { error });
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const deleteCustomer = createAsyncThunk(
    'customers/delete',
    async (passId, { rejectWithValue }) => {
        try {
            logger.info('Thunk: deleteCustomer - started', { passId });
            await deleteCustomerAPI(passId);
            return { passId };
        } catch (error) {
            logger.error('Thunk: deleteCustomer - error', { error });
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchCustomerRewards = createAsyncThunk(
    'customers/rewards',
    async (passId, { rejectWithValue }) => {
        try {
            logger.info('Thunk: fetchCustomerRewards - started');
            const res = await getCustomerRewards(passId);
            logger.info('Thunk: fetchCustomerRewards - success', { data: res.data });
            return res.data;
        } catch (error) {
            logger.error('Thunk: fetchCustomerRewards - error', { error });
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const initialState = {
    loading: false,
    error: null,
    list: [],
};

const customersSlice = createSlice({
    name: 'customers',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Use helper for fetchAllCustomers (standard pattern)
        addAsyncHandlers(builder, fetchAllCustomers, 'list');
        
        // Custom handlers for deleteCustomer (has custom list filtering logic)
        builder
            .addCase(deleteCustomer.pending, (state) => {
                logger.info('Reducer: deleteCustomer.pending');
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteCustomer.fulfilled, (state, action) => {
                logger.info('Reducer: deleteCustomer.fulfilled', { payload: action.payload });
                state.loading = false;
                state.error = null;

                // Custom logic: filter out deleted customer by passId
                state.list = state.list.filter(
                    (customer) => customer.wallet_pass_id !== action.payload.passId
                );
            })
            .addCase(deleteCustomer.rejected, (state, action) => {
                logger.error('Reducer: deleteCustomer.rejected', { error: action.payload });
                state.loading = false;
                state.error = action.payload?.error || action.error?.message || 'Failed to delete customer';
            });
        
        // Custom handlers for fetchCustomerRewards (has custom list mapping logic)
        builder
            .addCase(fetchCustomerRewards.pending, (state) => {
                logger.info('Reducer: fetchCustomerRewards.pending');
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCustomerRewards.fulfilled, (state, action) => {
                logger.info('Reducer: fetchCustomerRewards.fulfilled');
                state.loading = false;
                state.error = null;
                // Custom logic: update customer rewards in list
                state.list = state.list.map((customer) => {
                    if (
                        customer.customerEmail === action.payload.customerEmail &&
                        customer.platform === action.payload.platform
                    ) {
                        return {
                            ...customer,
                            rewards: action.payload.rewards,
                        };
                    }
                    return customer;
                });
            })
            .addCase(fetchCustomerRewards.rejected, (state, action) => {
                logger.error('Reducer: fetchCustomerRewards.rejected', { error: action.payload });
                state.loading = false;
                state.error = action.payload?.error || action.error?.message || 'Failed to fetch customer rewards';
            });
    },
});

export default customersSlice.reducer;
export const customersSliceAPI = {
    fetchAllCustomers,
    deleteCustomer,
    fetchCustomerRewards,
};
